<template>
  <div class="banner-management">
    <!-- 标题卡片 -->
    <div class="title-card">
      <div class="title-content">
        <h2 class="main-title">轮播图管理</h2>
        <p class="sub-title">管理网站轮播图片</p>
      </div>
      <div class="title-action">
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          新增轮播图
        </el-button>
      </div>
    </div>

    <!-- 列表卡片 -->
    <div class="list-card">
      <el-table
          :data="tableData.list"
          style="width: 100%"
          v-loading="tableData.loading"
          class="simple-table"
      >
        <el-table-column prop="sortOrder" label="排序" width="80" align="center" />
        <el-table-column label="预览图" width="120" align="center">
          <template slot-scope="scope">
            <img
                :src="scope.row.imageUrl"
                alt="预览"
                class="preview-img"
                @click="showImagePreview(scope.row.imageUrl)"
            />
          </template>
        </el-table-column>
        <el-table-column label="标题与描述" min-width="250">
          <template slot-scope="scope">
            <div class="text-content">
              <div class="title-text">{{ scope.row.title }}</div>
              <div class="desc-text">{{ scope.row.description }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="跳转店铺" width="180" align="center">
          <template slot-scope="scope">
            <div v-if="getStoreInfo(scope.row.linkUrl)" class="store-info">
              <div class="store-name">{{ getStoreInfo(scope.row.linkUrl).name }}</div>
              <div class="store-id">ID: {{ scope.row.linkUrl }}</div>
            </div>
            <span v-else class="no-store">-- 不跳转 --</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <span :class="['status', scope.row.isActive ? 'active' : 'inactive']">
              {{ scope.row.isActive ? '启用' : '禁用' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="160" align="center">
          <template slot-scope="scope">
            {{ formatTime(scope.row.addDataTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center">
          <template slot-scope="scope">
            <div class="action-btns">
              <el-button type="text" size="small" style="color: #F56C6C" @click="handleDelete(scope.row)">
                删除
              </el-button>
              <el-divider direction="vertical"></el-divider>
              <el-button type="text" size="small" @click="toggleStatus(scope.row)">
                {{ scope.row.isActive ? '禁用' : '启用' }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container" v-if="tableData.totalCount > tableData.pageSize">
        <el-pagination
            @current-change="handlePageChange"
            :current-page="tableData.page"
            :page-size="tableData.pageSize"
            layout="prev, pager, next, jumper"
            :total="tableData.totalCount"
            background
        />
      </div>
    </div>

    <!-- 新增轮播图弹窗 -->
    <el-dialog
        title="新增轮播图"
        :visible.sync="dialogVisible"
        width="500px"
        :close-on-click-modal="false"
        :append-to-body="true"
        custom-class="banner-edit-dialog"
    >
      <el-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          label-width="80px"
      >
        <el-form-item label="标题" prop="title">
          <el-input
              v-model="formData.title"
              placeholder="请输入轮播图标题"
              maxlength="50"
          />
        </el-form-item>

        <el-form-item label="描述" prop="description">
          <el-input
              v-model="formData.description"
              type="textarea"
              :rows="3"
              placeholder="请输入轮播图描述"
              maxlength="200"
          />
        </el-form-item>

        <el-form-item label="图片" prop="imageUrl">
          <div class="upload-container">
            <div class="image-select-area" @click="openImageSelector">
              <div v-if="formData.imageUrl" class="selected-image-preview">
                <img :src="formData.imageUrl" alt="预览" class="selected-image" />
                <div class="image-actions">
                  <el-button
                      type="text"
                      size="mini"
                      @click.stop="clearSelectedImage"
                      icon="el-icon-delete"
                  >
                    移除
                  </el-button>
                  <el-button
                      type="text"
                      size="mini"
                      @click.stop="openImageSelector"
                      icon="el-icon-refresh"
                  >
                    更换
                  </el-button>
                </div>
              </div>
              <div v-else class="image-placeholder">
                <i class="el-icon-picture-outline"></i>
                <div class="placeholder-text">点击选择图片</div>
                <div class="placeholder-subtext">支持 JPG、PNG 格式</div>
              </div>
            </div>

            <!-- 图片链接显示 -->
            <div class="image-url-input" v-if="formData.imageUrl">
              <el-input
                  v-model="formData.imageUrl"
                  placeholder="图片链接"
                  size="small"
                  readonly
              >
                <template #append>
                  <el-button
                      type="text"
                      size="mini"
                      @click="copyImageUrl"
                      icon="el-icon-document-copy"
                      title="复制链接"
                  />
                </template>
              </el-input>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="跳转店铺">
          <div class="store-select-container">
            <!-- 店铺信息展示 -->
            <div v-if="selectedStoreInfo" class="selected-store-info">
              <div class="store-info-content">
                <div class="store-name-line">
                  <i class="el-icon-s-shop store-icon"></i>
                  <span class="store-name">{{ selectedStoreInfo.name }}</span>
                </div>
                <div class="store-detail">
                  <span class="store-id">ID: {{ formData.linkUrl }}</span>
                </div>
              </div>
              <div class="store-actions">
                <el-button
                    type="text"
                    size="mini"
                    @click="openStoreSelector"
                    icon="el-icon-refresh"
                    title="更换店铺"
                >
                  更换
                </el-button>
                <el-button
                    type="text"
                    size="mini"
                    @click="clearSelectedStore"
                    icon="el-icon-close"
                    title="清空店铺"
                    style="color: #F56C6C"
                >
                  清空
                </el-button>
              </div>
            </div>

            <!-- 选择店铺按钮 -->
            <el-button
                v-else
                type="primary"
                plain
                icon="el-icon-search"
                @click="openStoreSelector"
                class="select-store-btn"
            >
              选择店铺
            </el-button>
          </div>
          <div class="store-tip">选择店铺后，点击轮播图将跳转到对应店铺</div>
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number
              v-model="formData.sortOrder"
              :min="1"
              :max="100"
              controls-position="right"
              placeholder="请输入排序值"
          />
          <span class="sort-tip">数字越小排序越靠前</span>
        </el-form-item>

        <el-form-item label="状态">
          <el-switch
              v-model="formData.isActive"
              active-text="启用"
              inactive-text="禁用"
          />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>

    <!-- 店铺选择弹窗 -->
    <el-dialog
        title="选择店铺"
        :visible.sync="storeDialogVisible"
        width="800px"
        top="5vh"
        :close-on-click-modal="false"
        :append-to-body="true"
        custom-class="store-selector-dialog"
    >
      <!-- 搜索区域 -->
      <div class="search-section" style="margin-bottom: 20px;">
        <el-input
            v-model="storeSearchText"
            placeholder="请输入店铺名称进行搜索"
            clearable
            @keyup.enter.native="searchStores"
            @clear="searchStores"
        >
          <template slot="append">
            <el-button icon="el-icon-search" @click="searchStores"></el-button>
          </template>
        </el-input>
      </div>

      <!-- 店铺列表 -->
      <div class="store-list-section">
        <el-table
            :data="storeList"
            v-loading="storeLoading"
            highlight-current-row
            @row-click="handleStoreRowClick"
            style="width: 100%"
            height="400"
        >
          <el-table-column prop="name" label="店铺名称" min-width="200">
            <template slot-scope="scope">
              <div class="store-name-cell">
                <span class="name-text">{{ scope.row.name }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="主图" width="120" align="center">
            <template slot-scope="scope">
              <div class="store-image-cell">
                <img
                    v-if="scope.row.image"
                    :src="scope.row.image"
                    alt="店铺主图"
                    class="store-image"
                    @click="showStoreImagePreview(scope.row.image)"
                />
                <div v-else class="no-image">暂无图片</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="更新时间" width="180" align="center">
            <template slot-scope="scope">
              <div class="update-time-cell">
                {{ formatStoreTime(scope.row.lastUpdateTime) }}
              </div>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="80" align="center">
            <template slot-scope="scope">
              <el-button
                  type="text"
                  size="small"
                  @click="selectStore(scope.row)"
              >
                选择
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container" style="margin-top: 20px;">
          <el-pagination
              @current-change="handleStorePageChange"
              :current-page="storePagination.page"
              :page-size="storePagination.pageSize"
              layout="prev, pager, next, jumper"
              :total="storePagination.totalCount"
              background
          />
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览弹窗 -->
    <el-dialog
        :visible.sync="previewVisible"
        append-to-body
        :close-on-click-modal="true"
        width="80%"
        custom-class="image-preview-dialog"
        top="5vh"
    >
      <img :src="previewImageUrl" alt="图片预览" class="full-preview-image" />
    </el-dialog>

    <!-- 店铺图片预览弹窗 -->
    <el-dialog
        :visible.sync="storePreviewVisible"
        append-to-body
        :close-on-click-modal="true"
        width="80%"
        custom-class="store-image-preview-dialog"
        top="5vh"
    >
      <img :src="storePreviewImageUrl" alt="店铺图片预览" class="full-preview-image" />
    </el-dialog>

    <!-- 图片选择器组件 -->
    <ImageSelector
        v-model="imageSelector.visible"
        :selected="imageSelector.selected"
        :max-count="1"
        @select="handleImageSelect"
        title="选择轮播图"
        width="900px"
    />
  </div>
</template>

<script>
import api from "@/utils/api";
import ImageSelector from "@/components/ImageSelector";

export default {
  name: 'Banner',

  components: {
    ImageSelector
  },

  data() {
    return {
      dialogVisible: false,
      previewVisible: false,
      storePreviewVisible: false,
      previewImageUrl: '',
      storePreviewImageUrl: '',

      // 图片选择器相关数据
      imageSelector: {
        visible: false,
        selected: []
      },

      // 店铺选择相关数据
      storeDialogVisible: false,
      storeSearchText: '',
      storeLoading: false,
      storeList: [],
      storePagination: {
        page: 1,
        pageSize: 10,
        totalCount: 0
      },

      formData: {
        title: '',
        description: '',
        imageUrl: '',
        linkUrl: '',        // 存储店铺ID
        sortOrder: 1,
        isActive: true
      },

      formRules: {
        title: [
          { required: true, message: '请输入标题', trigger: 'blur' },
          { min: 2, max: 50, message: '标题长度在2-50个字符之间', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请输入描述', trigger: 'blur' },
          { min: 5, max: 200, message: '描述长度在5-200个字符之间', trigger: 'blur' }
        ],
        imageUrl: [
          { required: true, message: '请选择图片', trigger: 'blur' }
        ]
      },

      // 表格数据 - 包含分页信息
      tableData: {
        list: [],
        totalCount: 0,
        page: 1,
        pageSize: 10,
        loading: false
      }
    }
  },

  computed: {
    // 选中的店铺信息（根据linkUrl从storeList中查找）
    selectedStoreInfo() {
      if (!this.formData.linkUrl || !this.storeList.length) return null;
      return this.storeList.find(store => store.id.toString() === this.formData.linkUrl.toString());
    }
  },

  created() {
    this.getCarouselList();
  },

  methods: {
    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '--';
      try {
        const date = new Date(timestamp);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      } catch (error) {
        console.error('格式化时间错误:', error);
        return '--';
      }
    },

    // 格式化店铺更新时间
    formatStoreTime(timestamp) {
      if (!timestamp) return '--';
      try {
        const date = new Date(timestamp);
        // 如果时间戳为0，返回"--"
        if (timestamp === 0) return '--';
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      } catch (error) {
        return '--';
      }
    },

    // 获取轮播图列表
    getCarouselList(page = 1) {
      console.log('开始加载轮播图列表，页码:', page);

      this.tableData.loading = true;
      if (page !== this.tableData.page) {
        this.tableData.page = page;
      }

      const params = {
        page: this.tableData.page,
        pageSize: this.tableData.pageSize
      };

      api.image_getCarouselList(params).then(res => {
        if (res.data.code === '000') {
          const data = res.data.data || {};
          const list = data.list || [];
          const totalCount = data.totalCount || 0;

          this.tableData.list = list;
          this.tableData.totalCount = totalCount;
          this.tableData.loading = false;

          // 如果当前页无数据且不是第一页，返回上一页
          if (this.tableData.list.length === 0 && this.tableData.page > 1) {
            this.tableData.page -= 1;
            this.getCarouselList(this.tableData.page);
          }
        } else {
          this.$message.error('获取轮播图列表失败：' + (res.data.message || '未知错误'));
          this.tableData.loading = false;
        }
      }).catch(err => {
        console.error('获取轮播图列表出错：', err);
        this.$message.error('获取轮播图列表失败，请重试');
        this.tableData.loading = false;
      });
    },

    // 页码变化
    handlePageChange(page) {
      if (!page || page < 1) return;
      this.tableData.page = page;
      this.getCarouselList(page);
    },

    // 显示图片预览
    showImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.previewVisible = true;
    },

    // 显示店铺图片预览
    showStoreImagePreview(imageUrl) {
      this.storePreviewImageUrl = imageUrl;
      this.storePreviewVisible = true;
    },

    // 获取店铺信息（从表格数据中查找）
    getStoreInfo(storeId) {
      if (!storeId || !this.tableData.list.length) return null;

      // 先从现有的storeList中查找
      if (this.storeList.length) {
        const store = this.storeList.find(s => s.id.toString() === storeId.toString());
        if (store) return store;
      }

      // 如果没有找到，返回一个基础对象
      return {
        name: '店铺ID: ' + storeId,
        id: storeId
      };
    },

    // 打开店铺选择器
    openStoreSelector() {
      this.storeDialogVisible = true;
      this.storeSearchText = '';
      this.storePagination.page = 1;
      this.searchStores();
    },

    // 搜索店铺
    searchStores() {
      this.storeLoading = true;

      const params = {
        page: this.storePagination.page,
        pageSize: this.storePagination.pageSize
      };

      // 添加搜索条件
      if (this.storeSearchText) {
        params.name = this.storeSearchText;
      }

      api.merchant_getMerchantList(params).then(res => {
        if (res.data.code === '000') {
          const data = res.data.data;
          this.storeList = data.list || [];
          this.storePagination.totalCount = data.totalCount || 0;
        } else {
          this.$message.error('获取店铺列表失败：' + (res.data.message || '未知错误'));
        }
      }).catch(err => {
        console.error('获取店铺列表出错：', err);
        this.$message.error('获取店铺列表失败，请重试');
      }).finally(() => {
        this.storeLoading = false;
      });
    },

    // 店铺分页变化
    handleStorePageChange(page) {
      if (!page || page < 1) return;
      this.storePagination.page = page;
      this.searchStores();
    },

    // 店铺行点击
    handleStoreRowClick(row) {
      this.selectStore(row);
    },

    // 选择店铺
    selectStore(store) {
      this.formData.linkUrl = store.id.toString();
      this.storeDialogVisible = false;
      this.$message.success('已选择店铺：' + store.name);
    },

    // 清空已选店铺
    clearSelectedStore() {
      this.formData.linkUrl = '';
      this.$message.info('已清空店铺选择');
    },

    handleAdd() {
      this.resetForm();
      this.dialogVisible = true;
    },

    handleDelete(row) {
      this.$confirm(`确定要删除轮播图 "${row.title}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        api.image_deleteCarousel({ id: row.id }).then(res => {
          if (res.data.code === '000') {
            this.$message.success('删除成功');
            this.getCarouselList(this.tableData.page);
          }
        })
      })
    },

    toggleStatus(row) {
      // 这里可以调用切换状态接口
      api.image_toggleCarouselStatus({ id: row.id }).then(res => {
        if (res.data.code === '000') {
          row.isActive = !row.isActive;
          this.$message.success(row.isActive ? '已启用' : '已禁用');
        }
      });

      // 先模拟切换成功
      // row.isActive = !row.isActive;
      this.$message.success(row.isActive ? '已启用（模拟）' : '已禁用（模拟）');
    },

    // 打开图片选择器
    openImageSelector() {
      // 设置当前已选的图片（如果有的话）
      if (this.formData.imageUrl) {
        this.imageSelector.selected = [{
          url: this.formData.imageUrl,
          name: this.formData.title || '轮播图'
        }];
      } else {
        this.imageSelector.selected = [];
      }

      this.imageSelector.visible = true;
    },

    // 处理图片选择
    handleImageSelect(images) {
      if (images && images.length > 0) {
        const image = images[0];
        this.formData.imageUrl = image.url || image.filepath || image.preview;

        // 如果当前没有标题，可以使用图片名称作为标题提示
        if (!this.formData.title && image.name) {
          this.formData.title = image.name;
        }

        this.$message.success('已选择图片');
      }
      this.imageSelector.visible = false;
    },

    // 清空已选图片
    clearSelectedImage() {
      this.formData.imageUrl = '';
      this.imageSelector.selected = [];
      this.$message.info('已移除图片');
    },

    // 复制图片链接
    copyImageUrl() {
      if (!this.formData.imageUrl) {
        this.$message.warning('没有图片链接可复制');
        return;
      }

      navigator.clipboard.writeText(this.formData.imageUrl)
          .then(() => {
            this.$message.success('图片链接已复制到剪贴板');
          })
          .catch(() => {
            // 降级方案
            const textarea = document.createElement('textarea');
            textarea.value = this.formData.imageUrl;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            this.$message.success('图片链接已复制');
          });
    },

    handleSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (valid) {
          console.log(this.formData);

          // 新增接口
          api.image_addCarousel(this.formData).then(res => {
            if (res.data.code === '000') {
              this.$message.success('新增成功');
              this.dialogVisible = false;
              this.getCarouselList(this.tableData.page);
            }
          })
        }
      });
    },

    resetForm() {
      this.formData = {
        title: '',
        description: '',
        imageUrl: '',
        linkUrl: '',
        sortOrder: this.tableData.list.length + 1,
        isActive: true
      };
      this.imageSelector.selected = [];

      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate();
      }
    }
  }
}
</script>

<style scoped lang="scss">
.banner-management {
  padding: 20px;
  background: #f0f2f5;
  min-height: calc(100vh - 84px);
}

// 标题卡片
.title-card {
  background: #fff;
  border-radius: 4px;
  padding: 20px 24px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  .title-content {
    .main-title {
      margin: 0 0 8px 0;
      font-size: 18px;
      font-weight: 500;
      color: #333;
    }

    .sub-title {
      margin: 0;
      color: #666;
      font-size: 14px;
    }
  }

  .title-action {
    .el-button {
      padding: 10px 20px;
      font-size: 14px;
      border-radius: 4px;
    }
  }
}

// 列表卡片
.list-card {
  background: #fff;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

// 表格相关样式
.preview-img {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.05);
  }
}

.text-content {
  .title-text {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    margin-bottom: 4px;
    line-height: 1.4;
  }

  .desc-text {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
  }
}

.status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 2px;
  font-size: 12px;

  &.active {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }

  &.inactive {
    background: #fff2f0;
    color: #ff4d4f;
    border: 1px solid #ffccc7;
  }
}

.action-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;

  .el-button {
    padding: 0;
    font-size: 12px;
    min-height: auto;
  }

  .el-divider {
    margin: 0 4px;
    background-color: #e8e8e8;
    height: 12px;
  }
}

// 分页样式
.pagination-container {
  margin-top: 20px;
  text-align: center;
}

// 弹窗相关样式
.upload-container {
  .image-select-area {
    width: 100%;
    min-height: 120px;
    border: 2px dashed #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s;
    overflow: hidden;

    &:hover {
      border-color: #409eff;
      background-color: rgba(64, 158, 255, 0.05);
    }

    .selected-image-preview {
      position: relative;
      width: 100%;
      height: 150px;

      .selected-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .image-actions {
        position: absolute;
        bottom: 10px;
        right: 10px;
        display: flex;
        gap: 10px;
        background: rgba(255, 255, 255, 0.9);
        padding: 4px 8px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        .el-button {
          padding: 0;
          font-size: 12px;
          color: #666;

          &:hover {
            color: #409eff;
          }

          i {
            margin-right: 4px;
          }
        }
      }
    }

    .image-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 150px;
      color: #909399;

      i {
        font-size: 40px;
        margin-bottom: 10px;
        color: #c0c4cc;
      }

      .placeholder-text {
        font-size: 14px;
        margin-bottom: 4px;
        color: #333;
      }

      .placeholder-subtext {
        font-size: 12px;
        color: #999;
      }
    }
  }

  .image-url-input {
    margin-top: 10px;

    :deep(.el-input-group__append) {
      padding: 0 8px;
      background: transparent;
      border: none;

      .el-button {
        padding: 0;
        color: #999;

        &:hover {
          color: #409eff;
        }
      }
    }
  }
}

// 店铺选择相关样式
.store-select-container {
  .selected-store-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;

    .store-info-content {
      flex: 1;

      .store-name-line {
        display: flex;
        align-items: center;
        margin-bottom: 6px;

        .store-icon {
          color: #409eff;
          margin-right: 8px;
          font-size: 16px;
        }

        .store-name {
          font-weight: 500;
          color: #333;
          font-size: 14px;
        }
      }

      .store-detail {
        font-size: 12px;
        color: #666;

        .store-id {
          color: #999;
        }
      }
    }

    .store-actions {
      display: flex;
      gap: 8px;

      .el-button {
        padding: 0;
        font-size: 12px;
      }
    }
  }

  .select-store-btn {
    width: 100%;
    height: 40px;
  }
}

.store-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.sort-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #999;
}

// 店铺列表样式
.store-name-cell {
  display: flex;
  align-items: center;

  .name-text {
    font-weight: 500;
  }
}

// 店铺图片样式
.store-image-cell {
  display: flex;
  justify-content: center;
  align-items: center;

  .store-image {
    width: 60px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #f0f0f0;
    cursor: pointer;
    transition: transform 0.3s;

    &:hover {
      transform: scale(1.05);
    }
  }

  .no-image {
    color: #999;
    font-size: 12px;
    text-align: center;
  }
}

// 更新时间样式
.update-time-cell {
  font-size: 12px;
  color: #666;
  text-align: center;
}

// 表格中店铺信息样式
.store-info {
  .store-name {
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
  }

  .store-id {
    font-size: 12px;
    color: #999;
  }
}

.no-store {
  color: #999;
  font-style: italic;
}

// 图片预览弹窗样式
.full-preview-image {
  width: 100%;
  height: auto;
  max-height: 70vh;
  object-fit: contain;
}

// 响应式调整
@media (max-width: 768px) {
  .banner-management {
    padding: 12px;
  }

  .title-card {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    .title-action {
      width: 100%;

      .el-button {
        width: 100%;
      }
    }
  }

  .list-card {
    padding: 12px;
    overflow-x: auto;
  }

  :deep(.banner-edit-dialog) {
    width: 90% !important;
    margin-top: 10vh !important;

    .el-dialog__body {
      max-height: 60vh;
    }
  }

  :deep(.store-selector-dialog) {
    width: 90% !important;
    margin-top: 5vh !important;
  }

  .upload-container {
    .image-select-area {
      .selected-image-preview {
        .image-actions {
          flex-direction: column;
          gap: 5px;
        }
      }
    }
  }

  .store-select-container {
    .selected-store-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      .store-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
  }
}

// 遮罩层层级修复
:deep(.store-selector-dialog) {
  z-index: 3000 !important;
}

:deep(.store-selector-dialog .el-dialog) {
  z-index: 3001 !important;
}

:deep(.store-selector-dialog .v-modal) {
  z-index: 2999 !important;
}

:deep(.banner-edit-dialog) {
  z-index: 2000 !important;
}

:deep(.banner-edit-dialog .el-dialog) {
  z-index: 2001 !important;
}

:deep(.banner-edit-dialog .v-modal) {
  z-index: 1999 !important;
}

:deep(.image-selector-dialog) {
  z-index: 4000 !important;
}

:deep(.image-selector-dialog .el-dialog) {
  z-index: 4001 !important;
}

:deep(.image-selector-dialog .v-modal) {
  z-index: 3999 !important;
}

:deep(.image-preview-dialog) {
  z-index: 5000 !important;
}

:deep(.image-preview-dialog .el-dialog) {
  z-index: 5001 !important;
}

:deep(.image-preview-dialog .v-modal) {
  z-index: 4999 !important;
}

:deep(.store-image-preview-dialog) {
  z-index: 5000 !important;
}

:deep(.store-image-preview-dialog .el-dialog) {
  z-index: 5001 !important;
}

:deep(.store-image-preview-dialog .v-modal) {
  z-index: 4999 !important;
}
</style>